import { Box, Icons, styled } from '@arpeggi/ui'
import { ComponentProps, createContext, forwardRef, useContext, useState } from 'react'
import { Card } from './Card'

const CollpasibleCardContext = createContext<{
  isCollapsed: boolean
  toggleIsCollapsed: () => void
}>({ isCollapsed: false, toggleIsCollapsed: () => {} })

const StyledRoot = styled(Card.Root, {
  padding: '20px'
})

const Header = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center'
})

const Title = styled('div', {
  textFamily: 'dm-bold',
  text: '18',
  color: '$gray0900'
})

const Subtitle = styled('div', {
  text: '14',
  color: '$white0600'
})

const Text = styled(Card.Text, {
  variants: {
    padded: {
      true: { paddingBottom: '24px', paddingTop: '5px' }
    }
  }
})

const Arrow = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(({ ...props }, ref) => {
  const state = useContext(CollpasibleCardContext)
  return (
    <Box ref={ref} {...props} css={{ paddingLeft: '16px' }}>
      <Icons.ChevronUp style={{ transform: state.isCollapsed && 'rotate(-180deg)' }} />
    </Box>
  )
})

const Content = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(
  ({ children, ...props }, ref) => {
    const state = useContext(CollpasibleCardContext)
    return (
      <Box column ref={ref} {...props}>
        {!state.isCollapsed && children}
      </Box>
    )
  }
)

const Trigger = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(
  ({ children, ...props }, ref) => {
    const state = useContext(CollpasibleCardContext)
    return (
      <Box
        ref={ref}
        {...props}
        onClick={(e) => {
          state.toggleIsCollapsed()
          props.onClick && props.onClick(e)
        }}
        css={{ cursor: 'pointer' }}
      >
        {children}
      </Box>
    )
  }
)

interface RootProps {
  isCollapsed?: boolean
}
export const Root = forwardRef<HTMLDivElement, RootProps & ComponentProps<typeof Card.Root>>(
  ({ isCollapsed, children, ...props }, ref) => {
    const [collapsed, setCollapsed] = useState<boolean>(isCollapsed === true)
    return (
      <StyledRoot ref={ref} {...props}>
        <CollpasibleCardContext.Provider
          value={{ isCollapsed: collapsed, toggleIsCollapsed: () => setCollapsed(!collapsed) }}
        >
          {children}
        </CollpasibleCardContext.Provider>
      </StyledRoot>
    )
  }
)

/**
 * Example CollapsibleCard Element:
 *
 * <CollapsibleCard.Root>
 *  <CollapsibleCard.Trigger>
 *   <CollapsibleCard.Header>
 *     <CollapsibleCard.Title>Dropdown!</CollapsibleCard.Title>
 *     <CollapsibleCard.Arrow />
 *   </CollapsibleCard.Header>
 *  </CollapsibleCard.Trigger>
 *  <CollapsibleCard.Content>
 *    <Box>I'm hidden until clicked</Box>
 *  </CollapsibleCard.Content>
 * </CollapsibleCard.Root>
 */
export const CollapsibleCard = {
  ...Card,
  Root,
  Title,
  Subtitle,
  Arrow,
  Trigger,
  Header,
  Content,
  Text
}
